// Edge-snap (přichycení plovoucích oken k okraji plochy) — pure, bez Reactu (testovatelné).
// Když se floating okno táhne za hlavičku k levému/pravému okraji plochy, detekuje cílovou zónu
// (polovina, nebo čtvrtina v rohu) a spočítá cílový rect. Port z prototypu (windows/app.jsx).
// Horní hrana = bez akce (maximalizace jen tlačítkem v hlavičce). Viz Spec sekce 5.

import type { WindowRect } from './windowManagerTypes';

export type SnapZone = 'left' | 'right' | 'tl' | 'tr' | 'bl' | 'br';

/** Šířka „citlivého" pásu u levé/pravé hrany (px). */
const EDGE_PX = 26;
/** Strop výšky rohové zóny (px) a její podíl z výšky plochy. */
const CORNER_MAX_PX = 120;
const CORNER_RATIO = 0.22;

export interface DeskRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Detekuje snap zónu z pozice kurzoru (client souřadnice) vůči ploše `desk`.
 * `null` = mimo snap zóny (běžné tažení). Horní hrana se nepoužívá.
 */
export function detectSnapZone(clientX: number, clientY: number, desk: DeskRect): SnapZone | null {
  const w = desk.width;
  const h = desk.height;
  if (w <= 0 || h <= 0) return null;

  const x = clientX - desk.left;
  const y = clientY - desk.top;
  const corner = Math.min(CORNER_MAX_PX, h * CORNER_RATIO);
  const nearL = x <= EDGE_PX;
  const nearR = x >= w - EDGE_PX;

  if (nearL && y <= corner) return 'tl';
  if (nearL && y >= h - corner) return 'bl';
  if (nearL) return 'left';
  if (nearR && y <= corner) return 'tr';
  if (nearR && y >= h - corner) return 'br';
  if (nearR) return 'right';
  return null;
}

/** Cílový rect pro zónu — polovina nebo čtvrtina plochy `dim`. */
export function snapRectForZone(zone: SnapZone, dim: { w: number; h: number }): WindowRect {
  const w = dim.w;
  const h = dim.h;
  const hw = Math.round(w / 2);
  const hh = Math.round(h / 2);
  switch (zone) {
    case 'left':
      return { x: 0, y: 0, w: hw, h };
    case 'right':
      return { x: hw, y: 0, w: w - hw, h };
    case 'tl':
      return { x: 0, y: 0, w: hw, h: hh };
    case 'bl':
      return { x: 0, y: hh, w: hw, h: h - hh };
    case 'tr':
      return { x: hw, y: 0, w: w - hw, h: hh };
    case 'br':
      return { x: hw, y: hh, w: w - hw, h: h - hh };
  }
}

/** localStorage klíč přepínače edge-snapu (UI ovládání zatím není — viz `isEdgeSnapEnabled`). */
export const EDGE_SNAP_STORAGE_KEY = 'phx-snap-edges';

/**
 * Je edge-snap zapnutý? Default `true`. Persist v localStorage (`phx-snap-edges`). Viditelný přepínač
 * zatím neděláme (rozhodnutí: vždy zapnuto), klíč i čtení jsou připravené pro budoucí toggle —
 * vypnutí = uložit hodnotu `'false'`. SSR/try-catch guard dle vzoru `shared/ui/theme/phxTheme.ts`.
 */
export function isEdgeSnapEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(EDGE_SNAP_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}
